import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams } from 'react-router'
import { useShareRule } from '../../api/useShareRules'
import Logo from '../../assets/images/logo.svg?react'
import { Separator } from '../ui/separator'
import { SidebarTrigger } from '../ui/sidebar'

export function AppHeader() {
  const { t } = useTranslation()
  const location = useLocation()
  const { ruleId } = useParams<{ ruleId: string }>()
  const { data: ruleData } = useShareRule(ruleId || '')

  // Map paths to section and page titles
  // Format: { path: { section: 'Section Name', page: 'Page Name' } }
  const pathConfig: Record<string, { section: string; page: string }> = {
    payments: {
      section: t('navigation.payments', { defaultValue: 'Zahlungen' }),
      page: t('navigation.paymentsOverview', {
        defaultValue: 'Zahlungsübersicht'
      })
    },
    'request-adyen-payment': {
      section: t('navigation.payments', { defaultValue: 'Zahlungen' }),
      page: t('navigation.requestPayment', {
        defaultValue: 'Zahlungen anfordern'
      })
    },
    'sub-merchants': {
      section: t('navigation.administration', { defaultValue: 'Verwaltung' }),
      page: t('navigation.subMerchants', { defaultValue: 'Sub Merchants' })
    },
    invoicing: {
      section: t('navigation.administration', { defaultValue: 'Verwaltung' }),
      page: t('navigation.monthlyBilling', {
        defaultValue: 'Monatsabrechnungen'
      })
    },
    'share-rules': {
      section: t('navigation.administration', { defaultValue: 'Verwaltung' }),
      page: t('navigation.shareRules', { defaultValue: 'Share Rules' })
    },
    'cost-coverage-codes': {
      section: t('navigation.administration', { defaultValue: 'Verwaltung' }),
      page: t('navigation.costCoverageCodes', {
        defaultValue: 'Cost Coverage Codes'
      })
    },
    'cost-coverage-reservations': {
      section: t('navigation.administration', { defaultValue: 'Verwaltung' }),
      page: t('navigation.costCoverageReservations', {
        defaultValue: 'Cost Coverage Reservations'
      })
    },
    'pending-transfers': {
      section: t('navigation.administration', { defaultValue: 'Verwaltung' }),
      page: t('navigation.pendingTransfers', {
        defaultValue: 'Pending Transfers'
      })
    }
  }

  // Generate breadcrumb items based on current path
  const getBreadcrumbs = () => {
    const paths = location.pathname.split('/').filter((path) => path)
    const result: { name: string; path?: string }[] = []

    // Build up the breadcrumb paths
    let currentPath = ''
    let sectionAdded = false

    for (let i = 0; i < paths.length; i++) {
      const path = paths[i]
      currentPath += `/${path}`

      // Skip 'admin' in breadcrumbs - it's not a real page
      if (path === 'admin') {
        continue
      }

      // Check if this path has a section/page config
      const config = pathConfig[path]
      if (config && !sectionAdded) {
        // Add section first (not clickable)
        result.push({ name: config.section })
        // Add page
        result.push({ name: config.page, path: currentPath })
        sectionAdded = true
        continue
      }

      // Special case for edit/:ruleId - use the rule title if available
      if (path === 'edit' && ruleId && ruleData) {
        result.push({
          name: t('breadcrumb.editRule', { defaultValue: 'Regel bearbeiten' })
        })
        continue
      }

      if (path === 'view') {
        result.push({
          name: t('breadcrumb.viewRule', { defaultValue: 'Regel ansehen' })
        })
        continue
      }

      // Special case for create route
      if (path === 'create') {
        result.push({
          name: t('breadcrumb.createRule', {
            defaultValue: 'Neue Regel erstellen'
          })
        })
        continue
      }

      // Special case for share-rules/:ruleId - use the rule title if available
      if (ruleId && path === ruleId && ruleData) {
        const title: string =
          ruleData.title ||
          (t('breadcrumb.untitledRule', {
            defaultValue: 'Unbenannte Regel'
          }) as string)
        const truncatedTitle =
          title.length > 20 ? `${title.substring(0, 20)}...` : title
        result.push({ name: truncatedTitle, path: currentPath })
        continue
      }

      // Skip if already handled by config
      if (config) {
        continue
      }

      // Fallback: convert to Title Case
      const name = path
        .replace(/[-_]/g, ' ')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')

      result.push({ name, path: currentPath })
    }

    return result
  }

  const breadcrumbs = getBreadcrumbs()

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-card shadow-xs">
      <div className="flex h-16 items-center px-6 gap-4">
        <div className="flex min-w-0 items-center gap-2">
          <SidebarTrigger className="-ml-1" data-testid="app-sidebar-toggle" />

          <Separator orientation="vertical" className="mx-1 h-4" />

          {/* Breadcrumb navigation */}
          <nav
            className="flex min-w-0 items-center"
            aria-label={
              t('navigation.breadcrumb_label', {
                defaultValue: 'Breadcrumb'
              }) as string
            }
            data-testid="app-breadcrumb"
          >
            {breadcrumbs.length > 0 ? (
              breadcrumbs.map((crumb, index) => (
                <div key={index} className="flex min-w-0 items-center">
                  {index > 0 && (
                    <ChevronRight className="h-4 w-4 mx-2 shrink-0 text-muted-foreground" />
                  )}
                  {index === 0 ? (
                    <span
                      className="truncate text-sm font-medium text-[#737373]"
                      title={crumb.name}
                    >
                      {crumb.name}
                    </span>
                  ) : index === breadcrumbs.length - 1 || !crumb.path ? (
                    <span
                      className="truncate text-sm font-medium"
                      title={crumb.name}
                    >
                      {crumb.name}
                    </span>
                  ) : (
                    <Link
                      to={crumb.path}
                      title={crumb.name}
                      className="truncate text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {crumb.name}
                    </Link>
                  )}
                </div>
              ))
            ) : (
              <Link to="/" className="flex items-center gap-2">
                <Logo height="40px" className="h-10" />
              </Link>
            )}
          </nav>
        </div>
      </div>
    </header>
  )
}
